import { eventBus } from '../core/EventBus';
import { Plant } from '../plants/Plant';
import { Lavender, PlantSpecies } from '../plants/PlantData';
import { PlantRenderer } from '../rendering/PlantRenderer';
import { InputHandler } from '../interaction/InputHandler';
import { UIManager } from '../ui/UIManager';
import { CurrencyManager } from '../economy/CurrencyManager';
import { iconFlower } from '../icons';

export class PlantManager {
  private plants: Plant[] = [];
  private uiManager!: UIManager;
  private selectedSpecies: PlantSpecies = Lavender;

  static readonly COL_STEP = 95;
  static readonly ROW_STEP = 70;

  constructor(
    private canvas: HTMLCanvasElement,
    uiManager: UIManager | null,
    private inputHandler: InputHandler,
    private plantRenderer: PlantRenderer,
    private currencyManager: CurrencyManager
  ) {
    if (uiManager) {
      this.uiManager = uiManager;
    }
    this.setupEventHandlers();
  }

  setUIManager(uiManager: UIManager): void {
    this.uiManager = uiManager;
  }

  selectSpecies(species: PlantSpecies): void {
    this.selectedSpecies = species;
  }

  getSelectedSpecies(): PlantSpecies {
    return this.selectedSpecies;
  }

  private setupEventHandlers(): void {
    eventBus.on('plant:place', (x: number, y: number) => this.handlePlantPlacement(x, y));
    eventBus.on('plant:remove', (plant: Plant, sellPrice: number) => this.handlePlantRemoval(plant, sellPrice));
    eventBus.on('plant:startPlacing', () => this.inputHandler.startPlacing());
    eventBus.on('plant:placingMode', (active: boolean) => {
      if (active) {
        this.uiManager.showNotification(`点击土壤种植${this.selectedSpecies.name} · 按 Esc 取消`, 3000);
      }
    });
    eventBus.on('plant:stageChanged', () => {
      this.uiManager.updateStageName('');
    });
  }

  private handlePlantPlacement(cssX: number, cssY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const cx = cssX * (this.canvas.width / rect.width);
    const cy = cssY * (this.canvas.height / rect.height);

    const soilLine = this.canvas.height * 0.75;
    if (cy < soilLine - 30) {
      this.uiManager.showNotification('请在土壤区域种植', 1500);
      return;
    }

    // 吸附到隐式网格
    const COL_STEP = PlantManager.COL_STEP;
    const ROW_STEP = PlantManager.ROW_STEP;
    const originX = this.canvas.width * 0.5;
    const originY = soilLine + 35;

    const col = Math.round((cx - originX) / COL_STEP);
    const row = Math.round((cy - originY) / ROW_STEP);
    const snappedX = originX + col * COL_STEP;
    const snappedY = originY + row * ROW_STEP;

    const MIN_DIST = 50;
    for (const p of this.plants) {
      const px = p.position.x * this.canvas.width;
      const py = p.position.y * this.canvas.height;
      const dist = Math.sqrt((snappedX - px) ** 2 + (snappedY - py) ** 2);
      if (dist < MIN_DIST) {
        this.uiManager.showNotification('这里已经种了一株植物', 1500);
        return;
      }
    }

    const normX = snappedX / this.canvas.width;
    const normY = snappedY / this.canvas.height;

    const plant = new Plant(this.selectedSpecies, { x: normX, y: normY });
    this.plants.push(plant);
    this.inputHandler.cancelPlacing();
    this.uiManager.showNotification(`${iconFlower({ size: 18 })} 种下了一株${this.selectedSpecies.name}`);
  }

  private handlePlantRemoval(plant: Plant, sellPrice: number): void {
    const idx = this.plants.indexOf(plant);
    if (idx >= 0) {
      this.plants.splice(idx, 1);
      plant.destroy();
      this.currencyManager.earn(sellPrice);
      this.uiManager.showNotification(`已铲除一株植物 · +${sellPrice}💰`, 1500);
    }
  }

  findNearestPlant(cssX: number, cssY: number, margin: number): Plant | null {
    const rect = this.canvas.getBoundingClientRect();
    const cx = cssX * (this.canvas.width / rect.width);
    const cy = cssY * (this.canvas.height / rect.height);

    let nearest: Plant | null = null;
    let nearestDist = Infinity;

    for (const plant of this.plants) {
      const px = plant.position.x * this.canvas.width;
      const py = plant.position.y * this.canvas.height;
      if (this.plantRenderer.hitTest(plant, px, py, cx, cy, margin)) {
        const dist = Math.sqrt((cx - px) ** 2 + (cy - py) ** 2);
        if (dist < nearestDist) {
          nearest = plant;
          nearestDist = dist;
        }
      }
    }

    return nearest;
  }

  getPlants(): Plant[] {
    return this.plants;
  }

  setPlants(plants: Plant[]): void {
    this.plants = plants;
  }

  clearAllPlants(): void {
    this.plants.forEach(plant => plant.destroy());
    this.plants = [];
  }

  addPlant(plant: Plant): void {
    this.plants.push(plant);
  }

  removePlantById(plantId: string): void {
    const idx = this.plants.findIndex(p => p.id === plantId);
    if (idx >= 0) {
      this.plants.splice(idx, 1);
    }
  }

  getPlantById(plantId: string): Plant | undefined {
    return this.plants.find(p => p.id === plantId);
  }

  getEmptyGridSlots(): Array<{ x: number; y: number }> {
    // 使用内部像素坐标，与 handlePlantPlacement 完全一致
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;
    const soilLine = canvasHeight * 0.75;
    const originX = canvasWidth * 0.5;
    const originY = soilLine + 35;
    const COL_STEP = PlantManager.COL_STEP;
    const ROW_STEP = PlantManager.ROW_STEP;
    const MIN_DIST = 50;

    const minCol = Math.floor((-COL_STEP - originX) / COL_STEP) - 1;
    const maxCol = Math.floor((canvasWidth + COL_STEP - originX) / COL_STEP) + 1;
    const minRow = Math.floor((originY - 20 - originY) / ROW_STEP) - 1;
    const maxRow = Math.floor((canvasHeight + ROW_STEP - originY) / ROW_STEP) + 1;

    const slots: Array<{ x: number; y: number }> = [];

    for (let col = minCol; col <= maxCol; col++) {
      for (let row = minRow; row <= maxRow; row++) {
        const sx = originX + col * COL_STEP;
        const sy = originY + row * ROW_STEP;
        if (sy < soilLine - 5) continue;

        let occupied = false;
        for (const p of this.plants) {
          const px = p.position.x * canvasWidth;
          const py = p.position.y * canvasHeight;
          if (Math.abs(sx - px) < MIN_DIST && Math.abs(sy - py) < MIN_DIST) {
            occupied = true;
            break;
          }
        }
        if (!occupied) {
          slots.push({ x: sx / canvasWidth, y: sy / canvasHeight });
        }
      }
    }

    return slots;
  }

  toJSON(): any[] {
    return this.plants.map(p => p.toJSON());
  }
}
